import { Injectable } from '@angular/core';

export interface BillingHighlightRange {
  rangeStart: Date;
  rangeEnd: Date;
}

export interface BillingMarkedDate {
  date: string;
  type: string;
  label: string;
}

@Injectable({ providedIn: 'root' })
export class BillingHighlightService {

  /**
   * Builds a Set of date strings (YYYY-MM-DD) to highlight as billable hours.
   * Uses API-supplied billingDates array directly.
   */
  getBillingDateSet(billingDates?: string[]): Set<string> {
  if (billingDates && billingDates.length > 0) {
    const normalized = billingDates.map(d => {
      const parts = d.split('-');
      if (parts.length === 3 && parts[0].length === 2) {
        // dd-MM-yyyy → YYYY-MM-DD
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }
      return d; // already YYYY-MM-DD, pass through
    });
    return new Set(normalized);
  }
  return new Set();
  }

  private toYMD(date: Date): string {
    return (
      date.getFullYear() +
      '-' +
      String(date.getMonth() + 1).padStart(2, '0') +
      '-' +
      String(date.getDate()).padStart(2, '0')
    );
  }

  clearBillingHighlight(): void {
    document.querySelectorAll('.mat-calendar-body-cell.last-working-day')
      .forEach(cell => cell.classList.remove('last-working-day'));
  }

  applyBillingHighlight(dateSet: Set<string>): void {
    setTimeout(() => {
      const calendarCells = document.querySelectorAll('.mat-calendar-body-cell');
      calendarCells.forEach((cell) => {
        const htmlCell = cell as HTMLElement;
        const ariaLabel = htmlCell.getAttribute('aria-label');
        if (!ariaLabel) return;
        const cellDate = new Date(ariaLabel);
        if (isNaN(cellDate.getTime())) return;

        const dateStr = this.toYMD(cellDate);
        htmlCell.classList.remove('last-working-day');

        if (dateSet.has(dateStr)) {
          const hasApiColor =
            htmlCell.classList.contains('paid') ||
            htmlCell.classList.contains('unpaid') ||
            htmlCell.classList.contains('halfpaid') ||
            htmlCell.classList.contains('halfunpaid') ||
            htmlCell.classList.contains('public-holiday');
          if (!hasApiColor) {
            htmlCell.classList.add('last-working-day');
          }
        }
      });
    }, 50);
  }

  refreshBillingHighlight(hasRangeSelection: boolean, dateSet: Set<string>): void {
    if (hasRangeSelection) {
      this.clearBillingHighlight();
      return;
    }
    if (dateSet.size === 0) return;
    this.applyBillingHighlight(dateSet);
  }

  getApiDateClass(type: string): string {
    if (type === 'paid') return 'paid';
    if (type === 'unpaid') return 'unpaid';
    if (type === 'halfpaid') return 'halfpaid';
    if (type === 'halfunpaid') return 'halfunpaid';
    if (type === 'public') return 'public-holiday';
    return '';
  }

  getBillingDateClass(
    d: Date,
    dateSet: Set<string>,
    hasRangeSelection: boolean,
    apiMarkedDates: BillingMarkedDate[],
    dateToString: (d: Date) => string,
  ): string {
    if (!hasRangeSelection && dateSet.size > 0) {
      const day = d.getDay();
      if (day === 0 || day === 6) return '';
      const dateStr = dateToString(d);
      if (dateSet.has(dateStr)) {
        const apiDate = apiMarkedDates.find((md) => md.date === dateStr);
        if (apiDate) {
          return this.getApiDateClass(apiDate.type);
        }
        return 'last-working-day';
      }
    }
    return '';
  }
}